import { useState, useMemo } from 'react';
import { X } from '@phosphor-icons/react';

/**
 * VariantPickerModal — popup om een specifieke variant te kiezen voor de POS/winkelwagen.
 *
 * Props:
 *  - artikel: { id, omschrijving, foto_url, dimensies, varianten, prijzen?, btw? }
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (variant) => void   -- callback met de geselecteerde variant {combo_key, combo, art_code, ean, voorraad, prijs_override, foto_url}
 *  - prijsGroep?: string (bv. 'kassa') -- voor prijs-display
 *  - preselectVariant?: { combo_key, combo } -- initialiseert keuze met deze combo (bv. uit EAN-scan)
 *  - allowZeroStock?: boolean -- als true: knop blijft werken bij voorraad=0 (bv. voor ontvangst-flow)
 *  - actieLabel?: string -- aangepast label voor bevestigen-knop (default: "Toevoegen")
 */
export default function VariantPickerModal({
  artikel, open, onClose, onSelect,
  prijsGroep = 'kassa',
  preselectVariant = null,
  allowZeroStock = false,
  actieLabel = 'Toevoegen',
  onSwitchToMatrix = null,  // Optionele callback: switch naar matrix-invoer (bv. voor documenten)
}) {
  const [keuze, setKeuze] = useState({}); // {kleur:'Zwart', maat:'M'}

  const dims = artikel?.dimensies || [];
  const vars = artikel?.varianten || [];

  // Reset bij openen + initialiseren met preselect
  useMemo(() => {
    if (open) {
      setKeuze(preselectVariant?.combo ? { ...preselectVariant.combo } : {});
    }
  }, [open, artikel?.id, preselectVariant?.combo_key]);

  // Filter varianten die matchen met huidige (gedeeltelijke) keuze
  const compatibele = useMemo(() => {
    return vars.filter(v => {
      const c = v.combo || {};
      return Object.entries(keuze).every(([k, val]) => !val || c[k] === val);
    });
  }, [vars, keuze]);

  // Welke waarden per dimensie zijn nog beschikbaar gegeven de huidige keuze?
  const beschikbaarPerDim = useMemo(() => {
    const map = {};
    for (const d of dims) {
      const others = Object.entries(keuze).filter(([k]) => k !== d.naam);
      const set = new Set();
      vars.forEach(v => {
        const c = v.combo || {};
        if (others.every(([k, val]) => !val || c[k] === val)) {
          if (c[d.naam] !== undefined) set.add(c[d.naam]);
        }
      });
      map[d.naam] = set;
    }
    return map;
  }, [dims, vars, keuze]);

  const exactMatch = compatibele.length === 1 && Object.keys(keuze).filter(k => keuze[k]).length === dims.length
    ? compatibele[0]
    : null;

  if (!open || !artikel) return null;

  const fotoSrc = (url) => url
    ? (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`)
    : null;

  const masterFoto = fotoSrc(artikel.foto_url);
  const previewFoto = exactMatch ? fotoSrc(exactMatch.foto_url) || masterFoto : masterFoto;

  const basisPrijs = (artikel.prijzen || {})[prijsGroep] || 0;
  const tonenPrijs = exactMatch
    ? (exactMatch.prijs_override !== null && exactMatch.prijs_override !== undefined ? exactMatch.prijs_override : basisPrijs)
    : basisPrijs;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="variant-picker-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 border-b border-blue-100 flex gap-3 items-center">
          {previewFoto ? (
            <img src={previewFoto} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-white shadow-sm flex-shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-2xl flex-shrink-0">📦</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-0.5">🎨 Kies variant</div>
            <div className="text-base font-bold text-gray-900 truncate">{artikel.omschrijving}</div>
            <div className="text-[11px] text-gray-500 font-mono">{artikel.art_code || ''}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Variantkeuze sluiten" data-testid="variant-picker-close" className="text-gray-400 hover:text-gray-600 self-start">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {dims.map(d => {
            const beschikbaar = beschikbaarPerDim[d.naam] || new Set();
            return (
              <div key={d.naam} data-testid={`var-pick-dim-${d.naam}`}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">{d.label || d.naam}</div>
                <div className="flex flex-wrap gap-1.5">
                  {(d.waardes || []).map(w => {
                    const isOff = !beschikbaar.has(w);
                    const isSel = keuze[d.naam] === w;
                    return (
                      <button
                        key={w}
                        type="button"
                        disabled={isOff}
                        onClick={() => setKeuze(k => ({ ...k, [d.naam]: isSel ? undefined : w }))}
                        className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 transition ${
                          isSel ? 'bg-blue-600 text-white border-blue-700 shadow-sm' :
                          isOff ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed line-through' :
                          'bg-white text-gray-800 border-gray-300 hover:border-blue-400'
                        }`}
                        data-testid={`var-pick-val-${d.naam}-${w}`}
                      >
                        {w}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {exactMatch && (
            <div className={`mt-3 px-3 py-2.5 rounded-lg border-2 ${
              exactMatch.voorraad > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'
            }`} data-testid="var-pick-summary">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs">
                  <div className="font-mono font-bold text-gray-900">{exactMatch.art_code}</div>
                  <div className="text-[10px] text-gray-500">{exactMatch.ean ? `EAN ${exactMatch.ean}` : 'geen EAN'}</div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${exactMatch.voorraad > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {exactMatch.voorraad > 0 ? `${exactMatch.voorraad} op voorraad` : 'Uitverkocht'}
                  </div>
                  <div className="text-[11px] text-gray-700">€ {Number(tonenPrijs).toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex gap-2 justify-between items-center flex-wrap">
          <div className="flex gap-2 items-center">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700" data-testid="var-pick-annuleer">
              Annuleren
            </button>
            {onSwitchToMatrix && (
              <button
                type="button"
                onClick={onSwitchToMatrix}
                className="text-sm inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 rounded"
                data-testid="var-pick-switch-matrix"
                title="Open matrix om meerdere varianten tegelijk te bestellen"
              >
                🔲 Meerdere varianten…
              </button>
            )}
          </div>
          <button
            onClick={() => exactMatch && onSelect?.(exactMatch)}
            disabled={!exactMatch || (!allowZeroStock && exactMatch.voorraad <= 0)}
            className="btn btn-primary btn-sm"
            data-testid="var-pick-bevestig"
          >
            {exactMatch
              ? ((!allowZeroStock && exactMatch.voorraad <= 0) ? 'Uitverkocht' : `✓ ${actieLabel} — ${exactMatch.art_code}`)
              : 'Kies eerst alle dimensies'}
          </button>
        </div>
      </div>
    </div>
  );
}
